import type { ButtonProps } from '@mui/material/Button';

import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from '@apollo/client/react';

import Button from '@mui/material/Button';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { dispatch } from 'src/state';
import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';
import * as ActionTypes from 'src/state/action-types';
import { LOGOUT_MUTATION, type LogoutMutationData } from 'src/graphql/user';

import { toast } from 'src/components/snackbar';

import { useAuthContext } from 'src/auth/hooks';
import { signOut as jwtSignOut } from 'src/auth/context/jwt/action';
import { signOut as amplifySignOut } from 'src/auth/context/amplify/action';
import { signOut as supabaseSignOut } from 'src/auth/context/supabase/action';
import { signOut as firebaseSignOut } from 'src/auth/context/firebase/action';

// ----------------------------------------------------------------------

const signOut =
  (CONFIG.auth.method === 'supabase' && supabaseSignOut) ||
  (CONFIG.auth.method === 'firebase' && firebaseSignOut) ||
  (CONFIG.auth.method === 'amplify' && amplifySignOut) ||
  jwtSignOut;

type Props = ButtonProps & {
  onClose?: () => void;
};

export function SignOutButton({ onClose, sx, ...other }: Props) {
  const router = useRouter();

  const { t } = useTranslate();

  const { checkUserSession } = useAuthContext();

  const { logout: signOutAuth0 } = useAuth0();

  const [logoutMutation] = useMutation<LogoutMutationData>(LOGOUT_MUTATION);

  const clearAllCookies = () => {
    const cookies = document.cookie.split(';');

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      if (name) {
        // Clear cookie for current path
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        // Clear cookie for parent domain
        const { hostname } = window.location;
        if (hostname !== 'localhost') {
          // Try with current domain
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`;
          // Try with dot-prefixed domain for subdomains
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname}`;

          // Try with parent domain if it's a subdomain
          const parts = hostname.split('.');
          if (parts.length > 2) {
            const parentDomain = parts.slice(-2).join('.');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parentDomain}`;
          }
        } else {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost`;
        }
      }
    });
  };

  const handleLogout = useCallback(async () => {
    try {
      // Dispatch Redux logout action to clear state
      dispatch({ type: ActionTypes.AUTH_LOGOUT_REQUEST });

      // Call the GraphQL logout mutation to clear cookies on the server
      await logoutMutation();

      await signOut();

      // Clear all cookies on the client side as a fallback
      clearAllCookies();

      await checkUserSession?.();

      onClose?.();

      // Redirect to the appropriate login page based on auth method
      const loginPath =
        (CONFIG.auth.method === 'jwt' && paths.auth.jwt.signIn) ||
        (CONFIG.auth.method === 'firebase' && paths.auth.firebase.signIn) ||
        (CONFIG.auth.method === 'amplify' && paths.auth.amplify.signIn) ||
        (CONFIG.auth.method === 'supabase' && paths.auth.supabase.signIn) ||
        '/';

      router.push(loginPath);
    } catch (error) {
      console.error(error);
      toast.error(t('Unable to logout!'));
    }
  }, [checkUserSession, logoutMutation, onClose, router, t]);

  const handleLogoutAuth0 = useCallback(async () => {
    try {
      // Dispatch Redux logout action to clear state
      dispatch({ type: ActionTypes.AUTH_LOGOUT_REQUEST });

      // Call the GraphQL logout mutation to clear cookies on the server
      await logoutMutation();

      await signOutAuth0();

      // Clear all cookies on the client side as a fallback
      clearAllCookies();

      onClose?.();

      // Auth0 will handle the redirect after logout
      router.push(paths.auth.auth0.signIn);
    } catch (error) {
      console.error(error);
      toast.error(t('Unable to logout!'));
    }
  }, [logoutMutation, onClose, router, signOutAuth0, t]);

  return (
    <Button
      fullWidth
      variant="soft"
      size="large"
      color="error"
      onClick={CONFIG.auth.method === 'auth0' ? handleLogoutAuth0 : handleLogout}
      sx={sx}
      {...other}
    >
      {t('Logout')}
    </Button>
  );
}
